import { DateTime } from 'luxon';
import { parseUrlParams } from 'utils/url-params';

export type ParserResult<T> = T | undefined;
export type Parser<T> = (input: string | Array<string>) => ParserResult<T>;
export type ParserDef<T> = { [P in keyof T]: Parser<T[P]> };

type MappedParseErrors<T> = { [_P in keyof T]: string | undefined };

interface CompleteParserResult<T> {
  result: Partial<T>;
  hasError: boolean;
  errors: MappedParseErrors<T>;
}

/**
 * Utility for parsing querystring
 * Returns strongly typed result based on parser definition
 *
 * Example usage:
 *
 * interface Filter {
 *   date: string;
 *   span: 'week' | 'day'
 * }
 *
 * const parseResult = parseWithDef<Filter>(
 * '?date=2019-01-01&span=week', {
 *    date: stringParser,
 *    span: spanParser
 *  }
 * );
 *
 * const parsedFilter: Partial<Filter> = parseResult.result;
 *
 * @param query
 * @param parserDefinition
 */
export const parseWithDef = <T>(
  query: string,
  parserDefinition: ParserDef<T>
): CompleteParserResult<T> => {
  const parsedUrlParams = parseUrlParams<T>(query);

  const result = {} as Partial<T>;
  const errors = {} as MappedParseErrors<T>;
  let hasError = false;

  for (const key in parsedUrlParams) {
    const item = parsedUrlParams[key];
    if (item !== undefined) {
      try {
        const matchingParser = parserDefinition[key];
        if (matchingParser) {
          const itemResult = matchingParser?.(item!);
          if (itemResult !== undefined) {
            result[key] = itemResult;
          } else {
            errors[key] = `Unable to parse ${key}`;
            hasError = true;
          }
        }
      } catch (err) {
        errors[key] = `Unable to parse ${key}, ${err.message}`;
        hasError = true;
      }
    }
  }

  return {
    errors,
    hasError,
    result,
  };
};

export const dateStringParser: Parser<string> = input => {
  if (Array.isArray(input)) {
    return undefined;
  }
  if (DateTime.fromISO(input).isValid) {
    return input;
  }
  throw Error('Invalid date');
};

export const numberArrayParser: Parser<Array<number>> = input => {
  if (Array.isArray(input)) {
    return input.filter(value => value !== '' && !isNaN(Number(value))).map(value => Number(value));
  } else {
    return !isNaN(Number(input)) ? [Number(input)] : undefined;
  }
};

export const numberParser: Parser<number> = input => {
  return !isNaN(Number(input)) ? Number(input) : undefined;
};

export const boolParser: Parser<boolean> = input => {
  if (Array.isArray(input)) {
    return undefined;
  }
  switch (input) {
    case 'true':
    case '1':
    case 'on':
    case 'yes':
      return true;
    default:
      return false;
  }
};

export function enumParser<T>(enumType: T, input: string | Array<string>): T[keyof T] | undefined {
  if (Array.isArray(input)) {
    return undefined;
  }
  return enumType[input as keyof T] || undefined;
}

export const timeParser: Parser<{ hour: number; minute: number; raw: string }> = input => {
  if (Array.isArray(input)) {
    input = input[0];
  }
  const parts = input.split(':').map(Number);
  if (parts.length === 2 && parts.every(part => !Number.isNaN(part))) {
    return { hour: parts[0], minute: parts[1], raw: input };
  }
  return undefined;
};
